// src/utils/useJudgeRaces.ts
//
// Běžící závody, které přihlášený skutečně rozhoduje – pro čtvrtou záložku
// „Rozhodčí“ ve spodní navigaci (fáze 3b, I1; rozhodnutí majitele 6′):
// hlavní rozhodčí závodu (headJudgeUids NEBO starý headJudgeUid) nebo
// rozhodčí stanovišť v races.liveJudgeUids. Správce bez těchto rolí záložku
// nevidí – bodování otevře ze závodu jako dosud.
//
// Jeden dotaz `races where status == "active"` (pravidla: races čte každý),
// žádné čtení stanovišť – liveJudgeUids zapisuje spuštění závodu a samooprava
// (fáze 1 a 2). Výsledek sdílí všechny instance hooku (spodní navigace se
// s výměnou rozložení obrazovky montuje znovu) a znovu se načte nejdřív po
// JUDGE_RACES_REFRESH_MS – při připojení hooku a po návratu do aplikace
// (visibilitychange). Chyba čtení nechá poslední známý seznam. Návrat do
// aplikace během běžícího dotazu ho po doběhnutí jednou zopakuje (dotaz mohl
// číst stav ještě před návratem, např. před přidáním náhradníka);
// refreshJudgeRaces načte hned (po spuštění závodu, po přidání sebe sama
// jako rozhodčího v nastavení).

import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { isHeadJudgeOf, isLiveJudgeOf, type LiveRaceLike } from "./livePermissions";
import { reportWarning } from "./reportError";

/** Závod v záložce / výběru závodu. */
export interface JudgeRace {
  id: string;
  name: string;
  /** "YYYY-MM-DD" jak je uložené (může chybět). */
  date: string;
}

/** Nejkratší odstup dvou dotazů (připojení hooku, návrat do aplikace). */
export const JUDGE_RACES_REFRESH_MS = 30000;

/**
 * Závody, které uživatel rozhoduje: běžící (status active) s živým
 * bodováním, kde je hlavním rozhodčím nebo v liveJudgeUids. Řazení podle
 * data a názvu.
 */
export function judgeRacesOf(
  docs: Array<{ id: string; data: LiveRaceLike & { name?: unknown; date?: unknown } }>,
  uid: string | null | undefined
): JudgeRace[] {
  if (!uid) return [];
  return docs
    .filter(({ data }) =>
      data.status === "active" &&
      !!data.liveScoring &&
      (isHeadJudgeOf(data, uid) || isLiveJudgeOf(data, uid)))
    .map(({ id, data }) => ({
      id,
      name: typeof data.name === "string" && data.name ? data.name : "Závod",
      date: typeof data.date === "string" ? data.date : "",
    }))
    .sort((a, b) => a.date.localeCompare(b.date) || a.name.localeCompare(b.name, "cs"));
}

// ── Sdílený stav (modul) ─────────────────────────────────────────────
interface Cache {
  uid: string;
  races: JudgeRace[];
  /** Čas posledního dotazu (i nepovedeného – chyba se neopakuje hned). */
  at: number;
}
let cache: Cache | null = null;
let inflight: { uid: string; promise: Promise<void> } | null = null;
/** Zopakovat dotaz pro tohoto uživatele hned po doběhnutí běžícího. */
let rerunFor: string | null = null;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((l) => l());

const sameRaces = (a: JudgeRace[], b: JudgeRace[]) =>
  a.length === b.length && a.every((r, i) => r.id === b[i].id && r.name === b[i].name && r.date === b[i].date);

function refresh(uid: string, force = false, rerunIfBusy = false): Promise<void> {
  if (inflight && inflight.uid === uid) {
    if (force || rerunIfBusy) rerunFor = uid;
    return inflight.promise;
  }
  if (!force && cache && cache.uid === uid && Date.now() - cache.at < JUDGE_RACES_REFRESH_MS) {
    return Promise.resolve();
  }
  const startedFor = uid;
  if (!cache || cache.uid !== uid) cache = { uid, races: [], at: 0 };
  cache.at = Date.now();
  // Synchronní chyba sestavení dotazu (nepřipravená databáze) jen varováním
  const promise = Promise.resolve()
    .then(() => getDocs(query(collection(db, "races"), where("status", "==", "active"))))
    .then((snap) => {
      if (!cache || cache.uid !== startedFor) return;
      const races = judgeRacesOf(
        snap.docs.map((d) => ({ id: d.id, data: d.data() as LiveRaceLike & { name?: unknown; date?: unknown } })),
        startedFor
      );
      if (!sameRaces(cache.races, races)) {
        cache = { uid: startedFor, races, at: cache.at };
        notify();
      }
    })
    .catch((err) => reportWarning("useJudgeRaces", err))
    .finally(() => {
      if (inflight && inflight.promise === promise) inflight = null;
      if (rerunFor === startedFor) {
        rerunFor = null;
        if (cache && cache.uid === startedFor) void refresh(startedFor, true);
      }
    });
  inflight = { uid, promise };
  return promise;
}

/** Jen pro testy: zapomenout sdílený stav mezi testy. */
export function resetJudgeRacesForTests(): void {
  cache = null;
  inflight = null;
  rerunFor = null;
  listeners.clear();
}

/**
 * Načte závody rozhodčího hned, bez odstupu (běží-li dotaz, zopakuje se po
 * něm) – po spuštění závodu a po přidání / odebrání sebe sama jako
 * rozhodčího, aby záložka a karta nečekaly na návrat do aplikace.
 */
export function refreshJudgeRaces(uid: string | null | undefined): void {
  if (!uid) return;
  void refresh(uid, true);
}

/**
 * Běžící závody, které uživatel rozhoduje (prázdné pole = žádné / ještě
 * nenačteno / nepřihlášený).
 */
export function useJudgeRaces(uid: string | null | undefined): JudgeRace[] {
  const [, force] = useState(0);
  useEffect(() => {
    if (!uid) return;
    const listener = () => force((n) => n + 1);
    listeners.add(listener);
    void refresh(uid);
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh(uid, false, true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      listeners.delete(listener);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [uid]);
  if (!uid || !cache || cache.uid !== uid) return [];
  return cache.races;
}
